import React, { useState, useEffect } from 'react';
import { collection, serverTimestamp, doc, runTransaction } from 'firebase/firestore'; // eslint-disable-line no-unused-vars
import { db, auth } from '../../firebase.js';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, LineController, Title, Tooltip, Legend, Filler } from 'chart.js';
import Swal from 'sweetalert2';
import { KpiCard } from '../../components/common/KpiCard.js';
import { DollarSign, TrendingUp, TrendingDown, Info, ChevronDown, ChevronUp } from 'lucide-react'; // eslint-disable-line no-unused-vars
import FinancialChart from './FinancialChart.js';
import '../../styles/Finanzas.css';
import '../../styles/Reportes.css';
import usePlatformConfig from '../../hooks/usePlatformConfig.js';
import TransactionHistory from '../../components/common/TransactionHistory.js'; // <-- 1. IMPORTAMOS EL NUEVO COMPONENTE

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, LineController, Title, Tooltip, Legend, Filler);

const CHART_RANGE_OPTIONS = [
    { value: '6m', label: '6M' },
    { value: '12m', label: '12M' },
    { value: 'ytd', label: 'YTD' },
    { value: 'all', label: 'Todo' }
];

const Finanzas = ({ profileData, transactions, financialSummary }) => {
    const [amount, setAmount] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const { config, loadingConfig } = usePlatformConfig();
    const [showAcceleratorWarning, setShowAcceleratorWarning] = useState(false);
    const [chartRange, setChartRange] = useState('6m');
    // El historial arranca colapsado: si no, en mobile ocupa toda la pantalla y tapa "Rendimiento
    // Mensual" que viene después. Se vuelve a colapsar solo al salir de la pantalla (es estado
    // local del componente, no se guarda en ningún lado).
    const [historyExpanded, setHistoryExpanded] = useState(false);
    
    // --- CORRECCIÓN CLAVE: Asegurar que los cálculos sean consistentes ---
    // Usamos un objeto seguro para `financialSummary` y calculamos el balance a partir de él.
    const safeSummary = financialSummary || { totalROI: 0, totalCommissions: 0, totalDeductions: 0, grossEarnings: 0 };
    // Mismo saldo que el Dashboard (withdrawableBalance del perfil, nunca negativo); ver useFinancialSummary.
    const withdrawableBalance = Number.isFinite(safeSummary.withdrawableBalance)
        ? safeSummary.withdrawableBalance
        : Math.max(0, safeSummary.grossEarnings + (safeSummary.totalRefunds || 0) - safeSummary.totalDeductions);
    
    const minimumWithdrawal = config?.minWithdrawalAmount || 50;
    const withdrawalFeeRate = config?.withdrawalFeeRate || 0.10;

    useEffect(() => {
        const parsedAmount = parseFloat(amount);
        if (!isNaN(parsedAmount) && parsedAmount > 0) {
            const remainingBalance = withdrawableBalance - parsedAmount;
            setShowAcceleratorWarning(remainingBalance < (config?.acceleratorFeeThreshold || 5.00));
        } else {
            setShowAcceleratorWarning(false);
        }
    }, [amount, withdrawableBalance, config]);

    const handleRequestWithdrawal = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        const parsedAmount = parseFloat(amount);

        if (isNaN(parsedAmount) || parsedAmount <= 0) {
            setError('Ingresa un monto válido.');
            setLoading(false);
            return;
        }
        if (parsedAmount < minimumWithdrawal) {
            setError(`El monto mínimo para retirar es de $${minimumWithdrawal}.`);
            setLoading(false);
            return;
        }
        if (parsedAmount > withdrawableBalance) {
            setError('No tienes saldo suficiente.');
            setLoading(false);
            return;
        }
        if (!profileData.walletAddress) {
            setError('Configura tu billetera en "Mi Perfil" para poder retirar.');
            setLoading(false);
            return;
        }

        const feeAmount = parsedAmount * withdrawalFeeRate;
        
        const confirmationResult = await Swal.fire({
            title: '¿Confirmar Solicitud?',
            html: `
                <div class="swal-summary">
                    <p>Monto a Retirar: <strong>$${parsedAmount.toFixed(2)}</strong></p>
                    <p>Comisión (${(withdrawalFeeRate * 100).toFixed(0)}%): <strong>$${feeAmount.toFixed(2)}</strong></p>
                    <p>Recibirás (aprox): <strong>$${(parsedAmount - feeAmount).toFixed(2)}</strong></p>
                    <hr/>
                    <p>Se enviará a la billetera:<br/><code>${profileData.walletAddress}</code></p>
                    ${showAcceleratorWarning ? `<p style="color: #f59e0b; font-size: 14px; margin-top: 1rem;"><strong>Sugerencia:</strong> Considera dejar un pequeño saldo para activar futuros aceleradores.</p>` : ''}
                </div>
            `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, solicitar',
            confirmButtonColor: 'var(--verde-botones)',
            cancelButtonText: 'Cancelar'
        });

        if (!confirmationResult.isConfirmed) {
            setLoading(false);
            return;
        }

        try {
            const user = auth.currentUser;
            const userProfileRef = doc(db, `users/${user.uid}/profile/data`);
            await runTransaction(db, async (transaction) => {
                const profileDoc = await transaction.get(userProfileRef);
                if (!profileDoc.exists()) throw new Error("Perfil no encontrado.");
                const currentBalance = profileDoc.data().withdrawableBalance || 0;
                if (currentBalance < parsedAmount) throw new Error("Saldo insuficiente.");
                transaction.update(userProfileRef, { withdrawableBalance: currentBalance - parsedAmount });
                const requestRef = doc(collection(db, 'withdraw_requests'));
                transaction.set(requestRef, {
                    userId: user.uid, userEmail: user.email,
                    amount: parsedAmount, fee: feeAmount,
                    netAmount: parsedAmount - feeAmount,
                    walletAddress: profileData.walletAddress, walletNetwork: profileData.walletNetwork,
                    status: 'pendiente', createdAt: serverTimestamp(),
                });
                // --- AÑADIDO: Registrar el hold en el historial del usuario ---
                const userTxRef = doc(collection(db, `users/${user.uid}/balance_transactions`));
                transaction.set(userTxRef, {
                  type: 'hold_retiro',
                  amount: -parsedAmount,
                  description: `Hold por solicitud de retiro`,
                  sourceTransactionId: requestRef.id,
                  createdAt: serverTimestamp(),
                });
            });
            Swal.fire({ title: '¡Solicitud Enviada!', icon: 'success', timer: 3000, timerProgressBar: true, showConfirmButton: false });
            setAmount('');
        } catch (err) {
            setError(err.message);
            Swal.fire('Error', `Ocurrió un problema: ${err.message}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="main-panel">
            <h2 className="section-title">Resumen Financiero</h2>
            
                <div className="kpi-grid">
                <KpiCard
                    title="Resultado acumulado (Bruto)"
                    value={`$${(safeSummary.grossEarnings || 0).toFixed(2)}`}
                    icon={<TrendingUp size={24} />}
                    color="#16a34a"
                    subtitle={`Resultados del acuerdo $${(safeSummary.totalROI || 0).toFixed(2)} · Comisiones $${(safeSummary.totalCommissions || 0).toFixed(2)}`}
                />

                <KpiCard
                    title="Deducciones Totales"
                    value={`-$${(safeSummary.totalDeductions || 0).toFixed(2)}`}
                    icon={<TrendingDown size={24} />} // Icono más apropiado
                    color="#dc2626"
                />

                    <KpiCard
                    title="Disponible para retiro"
                    value={`$${withdrawableBalance.toFixed(2)}`}
                    icon={<DollarSign size={24} />}
                    color="#3b82f6"
                    isHighlighted={true} // Opcional: una clase para destacarlo
                />
            </div>
            <div className="finance-section" style={{ marginTop: '2.5rem' }}>
                <div className="withdraw-panel">
                    <h3 className="section-subtitle">Solicitar Retiro</h3>
                    <div className="wallet-info-box">
                        <p className="wallet-info-label">El retiro se enviará al destino registrado:</p>
                        {profileData.walletAddress ? (
                            <div className="wallet-info-address">
                                <strong className="wallet-info-network">{profileData.walletNetwork || 'Red no especificada'}</strong>
                                <span className="wallet-info-address-value">{profileData.walletAddress}</span>
                            </div>
                        ) : (
                            <div className="message-box warning">
                                <span>No tienes un destino de retiro configurado. Ve a <strong>Mi Perfil</strong> para añadirlo.</span>
                            </div>
                        )}
                    </div>
                    <form onSubmit={handleRequestWithdrawal}>
                        <div className="input-group">
                            <label htmlFor="withdraw-amount">Importe a retirar (USD)</label>
                            <input id="withdraw-amount" type="number" min={minimumWithdrawal} step="0.01" value={amount} onChange={e => setAmount(e.target.value)} required disabled={!profileData.walletAddress || loadingConfig || !config?.withdrawalsEnabled} title={!config?.withdrawalsEnabled ? "Deshabilitado por el administrador" : ""}/>
                        </div>
                        {showAcceleratorWarning && (
                            <div className="accelerator-warning-box">
                                <Info size={20} />
                                <div>
                                    <strong>Sugerencia de participación</strong>
                                    <p>Para maximizar tus resultados, considera dejar un pequeño saldo para poder activar el Acelerador en tus próximos acuerdos.</p>
                                </div>
                            </div>
                        )}
                        <button type="submit" className="login-button" disabled={loading || !profileData.walletAddress || loadingConfig || !config?.withdrawalsEnabled} title={!config?.withdrawalsEnabled ? "Deshabilitado por el administrador" : ""}>
                            {loading ? 'Procesando...' : 'Solicitar Retiro'}
                        </button>
                    </form>
                    {error && <p className="message-box error" style={{marginTop: '1rem'}}>{error}</p>}
                </div>
                <div className="history-panel">
                    <button
                        type="button"
                        className="history-panel-toggle"
                        onClick={() => setHistoryExpanded(v => !v)}
                        aria-expanded={historyExpanded}
                    >
                        <h3 className="section-subtitle">Historial de Movimientos de Saldo</h3>
                        {historyExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </button>
                    {historyExpanded && (
                        /* --- 2. REEMPLAZAMOS TODA LA LÓGICA DE LA TABLA CON EL NUEVO COMPONENTE --- */
                        <TransactionHistory
                            transactions={transactions}
                            showTabs={true}
                            defaultTab="Todos"
                        />
                    )}
                </div>
            </div>
            <div className="finance-section-chart" style={{ marginTop: '2.5rem' }}>
                <div className="chart-card">
                    <div className="chart-card__header">
                        <h3 className="section-subtitle">Participación mensual</h3>
                        <div className="chart-range-controls">
                            {CHART_RANGE_OPTIONS.map(option => (
                                <button
                                    key={option.value}
                                    type="button"
                                    className={`chart-range-btn ${chartRange === option.value ? 'active' : ''}`}
                                    onClick={() => setChartRange(option.value)}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <FinancialChart transactions={transactions} range={chartRange} />
                </div>
            </div>
        </div>
    );
};

export default Finanzas;